import { ComponentConfig } from '@/components/componentRegistry';
import { Theme } from '@mui/material/styles';
import { Option } from './options';

interface PropertyStatusConfigParams {
  theme: Theme;
  t: (key: string) => string;
  propertyOptions: Option[];
  selectedPropertyStatus: string | null;
  onSelectPropertyStatus: (value: string) => void;
}

/**
 * Configuration for Property Status section
 * Follows responsive patterns:
 * - xs (mobile): Full width cards, vertical stack
 * - sm (600px+): 50% width cards with 8px gap
 * - md (900px+): Fixed 354.5px cards, horizontal row
 */
export const getPropertyStatusConfig = ({
  theme,
  t,
  propertyOptions,
  selectedPropertyStatus,
  onSelectPropertyStatus,
}: PropertyStatusConfigParams): ComponentConfig => {
  return {
    component: 'ColumnLayout',
    props: {
      spacing: 'lg',
      alignItems: 'center',
      fullWidth: true,
      sx: {
        animation: 'fadeIn 0.3s ease-in-out',
        '@keyframes fadeIn': {
          from: {
            opacity: 0,
            transform: 'translateY(-10px)',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      },
    },
    children: [
      // Title
      {
        component: 'Typography',
        props: {
          sx: {
            ...theme.typography.M3?.title?.large,
            fontWeight: 700,
            textAlign: 'center',
            color:
              theme.palette.Neutral?.['900'] || theme.palette.text?.primary,
            fontSize: { xs: theme.unit(5), md: theme.unit(6) },
          },
          children: t('loanSetup.propertyStatus.title'),
        },
      },
      // Options Row - Responsive layout
      {
        component: 'RowLayout',
        props: {
          spacing: 'md',
          justifyContent: 'center',
          wrap: true,
          sx: {
            width: '100%',
            gap: { xs: theme.unit(3), md: theme.unit(4) },
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
          },
        },
        children: propertyOptions.map((option) => ({
          component: 'CardInfo',
          props: {
            key: option.id,
            title: option.title,
            description: option.description,
            icon: option.icon,
            variant: 'selectable',
            value: option.id,
            selected: selectedPropertyStatus === option.id,
            onSelect: onSelectPropertyStatus,
            sx: {
              flex: {
                xs: 'none',
                sm: '1 1 calc(50% - 8px)',
                md: '0 1 354.5px',
              },
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: '100%', sm: '280px', md: '354.5px' },
              maxWidth: { xs: '100%', sm: '100%', md: '354.5px' },
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4],
              },
            },
          },
        })),
      },
    ],
  };
};
